import { TaxInfoModal } from "@client/components/billing/TaxInfoModal";
import { TransactionHistory } from "@client/components/credits/transaction-history";
import { BackButton, PageContainer } from "@client/components/ui";
import { Button } from "@client/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@client/components/ui/card";
import { Progress } from "@client/components/ui/progress";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@client/components/ui/tabs";
import { useUserLimits } from "@client/hooks/useUserLimits";
import { fireSuccessConfetti } from "@client/lib/confetti";
import { getPlanDetails, planHasFeature } from "@client/lib/plans";
import { trpc } from "@client/lib/trpc-client";
import { cn, formatCurrency } from "@client/lib/utils";
import {
	createFileRoute,
	Link,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import {
	Check,
	CreditCard,
	Crown,
	ExternalLink,
	Loader2,
	Package,
	ShieldCheck,
	Sparkles,
	Zap,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/(protected)/settings/billing")({
	component: BillingPage,
});

function BillingPage() {
	const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
		null,
	);
	const [showTaxModal, setShowTaxModal] = useState(false);
	const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">(
		"annual",
	);
	const [pendingAction, setPendingAction] = useState<{
		type: "purchase" | "subscribe";
		id: string;
	} | null>(null);

	const creditBalanceQuery = trpc.credits.getBalance.useQuery();
	const creditPackagesQuery = trpc.credits.getPackages.useQuery();
	const subscriptionPlansQuery = trpc.credits.getSubscriptionPlans.useQuery();
	const subscriptionQuery = trpc.user.getSubscription.useQuery();
	const taxInfoQuery = trpc.taxInfo.getTaxInfo.useQuery(undefined, {
		staleTime: 0, // Always fetch fresh — prevents stale cache bypassing the tax info gate
		throwOnError: false,
	});

	// True when we definitively know the user has no tax info (query settled)
	const needsTaxInfo = !taxInfoQuery.isLoading && !taxInfoQuery.data;
	const { planSiteLimit, siteCount, userPlan } = useUserLimits();
	const navigate = useNavigate();
	const search = useSearch({ strict: false });
	const trpcUtils = trpc.useUtils();
	const hasProcessedSuccess = useRef(false);

	// Get plan details using centralized utility
	const planDetails = getPlanDetails(userPlan);

	// Subscription verification mutation
	const verifySubscriptionMutation =
		trpc.credits.verifySubscription.useMutation({
			onSuccess: async (data) => {
				if (data.success) {
					fireSuccessConfetti();

					// X Ads conversion event
					window.twq?.("event", "tw-rccfj-rciuu", {
						value: (data.amountTotal ?? 0) / 100,
						currency: data.currency ?? "USD",
						conversion_id: data.sessionId,
					});

					toast.success(`Welcome to ${data.planName}! 🎉`, {
						description: "Your subscription is now active.",
						duration: 6000,
					});
					// Refresh subscription and credits data, then clean URL
					await Promise.all([
						trpcUtils.user.getSubscription.invalidate(),
						trpcUtils.credits.getBalance.invalidate(),
					]);
					navigate({
						to: "/settings/billing",
						replace: true,
					});
				}
			},
			onError: (error) => {
				console.error("Subscription verification failed:", error);
				toast.error("Subscription verification", {
					description:
						"We couldn't verify your subscription right now. Your plan will update shortly.",
				});
				// Still clean URL on error
				navigate({
					to: "/settings/billing",
					replace: true,
				});
			},
		});

	// Handle URL params from Stripe redirect
	useEffect(() => {
		if (hasProcessedSuccess.current) return;

		const params = search as Record<string, string | undefined>;
		const subscriptionStatus = params.subscription;
		const sessionId = params.session_id;

		if (subscriptionStatus === "success" && sessionId) {
			hasProcessedSuccess.current = true;
			verifySubscriptionMutation.mutate({ sessionId });
		} else if (subscriptionStatus === "canceled") {
			hasProcessedSuccess.current = true;
			toast.info("Checkout canceled", {
				description: "You can try again whenever you're ready.",
			});
			navigate({
				to: "/settings/billing",
				replace: true,
			});
		}
	}, [search, navigate, verifySubscriptionMutation]);

	// Stripe checkout mutations
	const purchaseCreditsMutation = trpc.credits.purchaseCredits.useMutation({
		onSuccess: (data) => {
			if (data.url) {
				// Redirect to Stripe Checkout
				window.location.href = data.url;
			} else {
				toast.error("Failed to create checkout session. Please try again.");
				setSelectedPackageId(null);
			}
		},
		onError: (error) => {
			toast.error(error.message || "Failed to create checkout session");
			setSelectedPackageId(null);
		},
	});

	const subscriptionCheckoutMutation =
		trpc.credits.createSubscriptionCheckout.useMutation({
			onSuccess: (data) => {
				if (data.url) {
					// Redirect to Stripe Checkout
					window.location.href = data.url;
				} else {
					toast.error(
						"Failed to create subscription checkout. Please try again.",
					);
					setSubscribingPlanId(null);
				}
			},
			onError: (error) => {
				toast.error(error.message || "Failed to create subscription checkout");
				setSubscribingPlanId(null);
			},
		});

	const [subscribingPlanId, setSubscribingPlanId] = useState<string | null>(
		null,
	);

	const portalMutation = trpc.credits.createPortalSession.useMutation({
		onSuccess: (data) => {
			window.location.href = data.url;
		},
		onError: (error) => {
			toast.error(error.message || "Failed to open billing portal");
		},
	});

	// Execute actual purchase (called after tax info check)
	const executePurchase = useCallback(
		(packageId: string) => {
			setSelectedPackageId(packageId);
			purchaseCreditsMutation.mutate({ packageId });
		},
		[purchaseCreditsMutation],
	);

	// Execute actual subscription (called after tax info check)
	const executeSubscribe = useCallback(
		(planId: string) => {
			setSubscribingPlanId(planId);
			subscriptionCheckoutMutation.mutate({ planId, billingCycle });
		},
		[subscriptionCheckoutMutation, billingCycle],
	);

	// Handle action after tax info is provided
	const handleTaxInfoSuccess = useCallback(() => {
		setShowTaxModal(false);
		if (pendingAction) {
			if (pendingAction.type === "purchase") {
				executePurchase(pendingAction.id);
			} else if (pendingAction.type === "subscribe") {
				executeSubscribe(pendingAction.id);
			}
			setPendingAction(null);
		}
	}, [pendingAction, executePurchase, executeSubscribe]);

	// Check tax info before purchase
	const handlePurchase = useCallback(
		(packageId: string) => {
			if (needsTaxInfo) {
				setPendingAction({ type: "purchase", id: packageId });
				setShowTaxModal(true);
				return;
			}
			executePurchase(packageId);
		},
		[needsTaxInfo, executePurchase],
	);

	// Check tax info before subscription
	const handleSubscribe = useCallback(
		(planId: string) => {
			if (needsTaxInfo) {
				setPendingAction({ type: "subscribe", id: planId });
				setShowTaxModal(true);
				return;
			}
			executeSubscribe(planId);
		},
		[needsTaxInfo, executeSubscribe],
	);

	return (
		<PageContainer maxWidth="narrow">
			<BackButton to="/settings" label="Back to Settings" />

			{/* Header */}
			<div className="mb-8">
				<div className="flex items-center gap-3">
					<CreditCard className="h-8 w-8 text-primary" />
					<h1 className="font-semibold text-3xl tracking-tight">Billing</h1>
				</div>
				<p className="mt-1 text-muted-foreground">
					Manage credits, subscriptions, and invoices
				</p>
			</div>

			{/* Current Plan Card */}
			<Card className="mb-8">
				<CardHeader>
					<div className="flex items-center justify-between">
						<div>
							<CardTitle>Current Plan</CardTitle>
							<CardDescription>Your subscription and limits</CardDescription>
						</div>
						<div className="flex items-center gap-2">
							{userPlan !== "free" && (
								<Button
									variant="outline"
									size="sm"
									onClick={() => portalMutation.mutate()}
									disabled={portalMutation.isPending}
								>
									{portalMutation.isPending ? (
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									) : (
										<ExternalLink className="mr-2 h-4 w-4" />
									)}
									Manage Subscription
								</Button>
							)}
							<Button
								variant="outline"
								size="sm"
								onClick={() => {
									document
										.getElementById("subscription-plans")
										?.scrollIntoView({ behavior: "smooth" });
									document.getElementById("subscription-tab")?.click();
								}}
							>
								Upgrade
							</Button>
						</div>
					</div>
				</CardHeader>
				<CardContent>
					<div className="flex flex-wrap items-center gap-8">
						<div>
							<div className="mb-1 flex items-center gap-2">
								<Crown className="h-5 w-5 text-primary" />
								<span className="font-semibold text-xl">
									{planDetails.displayName}
								</span>
							</div>
							<p className="text-muted-foreground text-sm">
								{planDetails.priceText} •{" "}
								{planDetails.monthlyCredits > 0
									? `${planDetails.monthlyCredits} credits/month`
									: "Pay-as-you-go credits"}
							</p>
						</div>

						<div className="flex flex-col gap-1 text-sm">
							<div className="flex items-center gap-2">
								<span className="text-muted-foreground">Site limit:</span>
								<span
									className={
										siteCount >= planSiteLimit
											? "font-medium text-destructive"
											: ""
									}
								>
									{siteCount}/{planSiteLimit}
								</span>
							</div>
							<div className="flex items-center gap-2">
								<span className="text-muted-foreground">Credit rollover:</span>
								<span
									className={
										planHasFeature(userPlan, "rollover")
											? "text-green-600 dark:text-green-400"
											: "text-muted-foreground"
									}
								>
									{planHasFeature(userPlan, "rollover")
										? "Enabled"
										: "Disabled"}
								</span>
							</div>
							<div className="flex items-center gap-2">
								<span className="text-muted-foreground">Auto updates:</span>
								<span
									className={
										planHasFeature(userPlan, "automatic")
											? "text-green-600 dark:text-green-400"
											: "text-muted-foreground"
									}
								>
									{planHasFeature(userPlan, "automatic") ? "Enabled" : "Manual"}
								</span>
							</div>
						</div>
					</div>

					{userPlan !== "free" && (
						<div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
							<p className="text-muted-foreground text-sm">
								{(() => {
									const renewDate =
										subscriptionQuery.data?.nextBillingDate ??
										subscriptionQuery.data?.creditsResetDate;
									if (!renewDate) return "Subscription active";
									return (
										<>
											Your subscription will auto-renew on{" "}
											<span className="font-medium text-foreground">
												{new Date(renewDate).toLocaleDateString("en-US", {
													month: "long",
													day: "numeric",
													year: "numeric",
												})}
											</span>
											{subscriptionQuery.data?.billingCycle === "annual" && (
												<span className="ml-1 text-muted-foreground">
													(annual)
												</span>
											)}
										</>
									);
								})()}
							</p>
							<Link
								to="/settings/invoices"
								className="inline-flex items-center gap-1.5 text-primary text-sm underline-offset-4 hover:underline"
							>
								<ExternalLink className="h-3.5 w-3.5" />
								View invoices
							</Link>
						</div>
					)}
				</CardContent>
			</Card>

			<div className="grid gap-6 md:grid-cols-2">
				<Card>
					<CardHeader>
						<CardTitle>Credit Balance</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="flex items-end gap-2">
							<span className="font-semibold text-4xl text-primary">
								{creditBalanceQuery.isLoading
									? "..."
									: (creditBalanceQuery.data?.balance?.balance ?? 0)}
							</span>
							<span className="text-muted-foreground text-sm">
								credits available
							</span>
						</div>

						<div className="mt-4 space-y-3 text-sm">
							<div className="flex justify-between">
								<span className="text-muted-foreground">Total purchased</span>
								<span>
									{creditBalanceQuery.data?.balance?.totalPurchased ?? 0}
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-muted-foreground">Total used</span>
								<span>{creditBalanceQuery.data?.balance?.totalUsed ?? 0}</span>
							</div>
							{creditBalanceQuery.data?.balance && (
								<div className="space-y-1.5">
									<div className="flex justify-between text-muted-foreground text-xs">
										<span>Usage</span>
										<span>
											{Math.round(
												(creditBalanceQuery.data.balance.balance /
													(creditBalanceQuery.data.balance.totalPurchased ||
														100)) *
													100,
											)}
											%
										</span>
									</div>
									<Progress
										value={
											(creditBalanceQuery.data.balance.balance /
												(creditBalanceQuery.data.balance.totalPurchased ||
													100)) *
											100
										}
									/>
								</div>
							)}
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>Pricing</CardTitle>
					</CardHeader>
					<CardContent>
						<ul className="space-y-4">
							<li className="flex items-start gap-3">
								<ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
								<div>
									<p className="font-medium text-sm">
										1 credit = 1 URL processed
									</p>
									<p className="text-muted-foreground text-sm">
										Credits are only used when URLs are successfully processed
									</p>
								</div>
							</li>
							<li className="flex items-start gap-3">
								<Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
								<div>
									<p className="font-medium text-sm">
										Small sites are affordable
									</p>
									<p className="text-muted-foreground text-sm">
										A typical 25-URL site costs only 25 credits (~$0.25-0.50)
									</p>
								</div>
							</li>
						</ul>
					</CardContent>
				</Card>
			</div>

			<h2 className="mt-12 mb-6 font-semibold text-2xl" id="subscription-plans">
				Plans & Credits
			</h2>

			<Tabs defaultValue="subscription" className="w-full">
				<TabsList className="mb-6">
					<TabsTrigger value="subscription" id="subscription-tab">
						Subscription
					</TabsTrigger>
					<TabsTrigger value="credits">Pay as You Go</TabsTrigger>
				</TabsList>

				<TabsContent value="credits">
					<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
						{creditPackagesQuery.isLoading ? (
							<div className="col-span-full flex justify-center py-12">
								<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
							</div>
						) : (
							creditPackagesQuery.data
								?.filter(
									(pkg) => !pkg.name.toLowerCase().includes("subscription"),
								)
								.map((pkg) => (
									<Card
										key={pkg.id}
										className={`border-2 dark:border-gray-700 ${
											selectedPackageId === pkg.id ? "ring-2 ring-primary" : ""
										}`}
									>
										<CardHeader>
											<CardTitle className="flex items-center gap-2 dark:text-gray-100">
												<Package className="h-5 w-5" />
												{pkg.name}
												{pkg.id === "package-500" && (
													<span className="rounded-md bg-primary/10 px-2 py-1 font-medium text-primary text-xs dark:bg-primary/20 dark:text-primary">
														Popular
													</span>
												)}
											</CardTitle>
											<CardDescription className="dark:text-gray-400">
												{pkg.credits} credits
											</CardDescription>
										</CardHeader>
										<CardContent>
											<div className="mb-2 flex items-end gap-1">
												<span className="font-bold text-3xl dark:text-gray-100">
													{formatCurrency(pkg.priceCents / 100)}
												</span>
												<span className="text-muted-foreground dark:text-gray-400">
													one-time
												</span>
											</div>
											<p className="text-muted-foreground text-sm dark:text-gray-400">
												${(pkg.priceCents / 100 / pkg.credits).toFixed(3)} per
												credit
											</p>
										</CardContent>
										<CardFooter>
											<Button
												className="w-full"
												onClick={() => handlePurchase(pkg.id)}
												disabled={
													selectedPackageId === pkg.id || taxInfoQuery.isLoading
												}
											>
												{selectedPackageId === pkg.id ? (
													<>
														<Loader2 className="mr-2 h-4 w-4 animate-spin" />
														Processing...
													</>
												) : (
													<>
														<CreditCard className="mr-2 h-4 w-4" />
														Purchase
													</>
												)}
											</Button>
										</CardFooter>
									</Card>
								))
						)}
					</div>

					<div className="mt-8 rounded-lg border bg-muted/50 p-4 dark:border-gray-700 dark:bg-gray-900/30">
						<p className="text-sm dark:text-gray-300">
							Credits never expire and can be used for any number of sites.
							Purchase once and use whenever you need them.
						</p>
					</div>
				</TabsContent>

				<TabsContent value="subscription">
					<div className="mb-6 rounded-lg border bg-muted/5 p-4 dark:border-gray-700 dark:bg-gray-900/30">
						<h3 className="mb-2 font-medium text-lg dark:text-gray-100">
							Why Subscribe?
						</h3>
						<ul className="space-y-1 text-sm dark:text-gray-300">
							<li className="flex items-start gap-2">
								<Check className="mt-0.5 h-4 w-4 text-primary" />
								<span>
									Increase your site limit beyond the free plan's 1 site
								</span>
							</li>
							<li className="flex items-start gap-2">
								<Check className="mt-0.5 h-4 w-4 text-primary" />
								<span>Get automatic updates for your llms.txt files</span>
							</li>
							<li className="flex items-start gap-2">
								<Check className="mt-0.5 h-4 w-4 text-primary" />
								<span>
									Credits roll over month-to-month (up to 2x your monthly
									allocation)
								</span>
							</li>
						</ul>
					</div>

					{/* Billing cycle toggle */}
					<div className="mb-6 flex items-center justify-center">
						<div className="flex items-center gap-1 rounded-full border bg-muted/30 p-1">
							<button
								type="button"
								onClick={() => setBillingCycle("monthly")}
								className={cn(
									"rounded-full px-5 py-1.5 font-medium text-sm transition-all",
									billingCycle === "monthly"
										? "bg-background text-foreground shadow"
										: "text-muted-foreground hover:text-foreground",
								)}
							>
								Monthly
							</button>
							<button
								type="button"
								onClick={() => setBillingCycle("annual")}
								className={cn(
									"flex items-center gap-1.5 rounded-full px-5 py-1.5 font-medium text-sm transition-all",
									billingCycle === "annual"
										? "bg-background text-foreground shadow"
										: "text-muted-foreground hover:text-foreground",
								)}
							>
								Annual
								<span className="rounded-full bg-green-500/15 px-1.5 py-0.5 font-semibold text-green-600 text-xs dark:bg-green-500/20 dark:text-green-400">
									Save 17%
								</span>
							</button>
						</div>
					</div>
					<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
						{subscriptionPlansQuery.isLoading ? (
							<div className="col-span-full flex justify-center py-12">
								<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
							</div>
						) : (
							(subscriptionPlansQuery.data ?? []).map((plan) => {
								const userBillingCycle =
									subscriptionQuery.data?.billingCycle ?? "monthly";
								const isCurrentPlan =
									userPlan === plan.id && userBillingCycle === billingCycle;
								const isSubscribing = subscribingPlanId === plan.id;

								// Determine upgrade vs downgrade
								const planTierOrder = [
									"free",
									"starter",
									"professional",
									"business",
									"agency",
								];
								const currentTierIndex = planTierOrder.indexOf(userPlan);
								const thisTierIndex = planTierOrder.indexOf(plan.id);
								const isUpgrade = thisTierIndex > currentTierIndex;
								const isDowngrade = thisTierIndex < currentTierIndex;
								const buttonLabel = isCurrentPlan
									? "Current Plan"
									: isUpgrade
										? "Upgrade"
										: isDowngrade
											? "Downgrade"
											: "Subscribe";

								// Annual pricing: 10× monthly (2 months free)
								const annualTotal = plan.priceCents * 10;
								const annualMonthlyEquiv = annualTotal / 12;
								const displayPrice =
									billingCycle === "annual"
										? formatCurrency(annualMonthlyEquiv / 100)
										: plan.priceDisplay.replace("/month", "");

								return (
									<Card
										key={plan.id}
										className={`flex flex-col border-2 dark:border-gray-700 ${plan.popular ? "ring-2 ring-primary" : ""}`}
									>
										<CardHeader>
											<div className="flex items-start justify-between">
												<div>
													<CardTitle className="dark:text-gray-100">
														{plan.name}
													</CardTitle>
													<CardDescription className="dark:text-gray-400">
														{plan.monthlyCredits} credits/month
													</CardDescription>
												</div>
												{plan.popular && (
													<span className="rounded-md bg-primary/10 px-2 py-1 font-medium text-primary text-xs dark:bg-primary/20 dark:text-primary">
														Popular
													</span>
												)}
											</div>
										</CardHeader>
										<CardContent className="flex-1">
											<div className="mb-4">
												<div className="flex items-end gap-1">
													<span className="font-bold text-3xl dark:text-gray-100">
														{displayPrice}
													</span>
													<span className="text-muted-foreground dark:text-gray-400">
														/month
													</span>
												</div>
												{billingCycle === "annual" && (
													<div className="mt-1 flex items-center gap-1.5">
														<Zap className="h-3.5 w-3.5 text-green-500" />
														<span className="text-green-600 text-xs dark:text-green-400">
															{formatCurrency(annualTotal / 100)}/year · 2
															months free
														</span>
													</div>
												)}
											</div>

											<ul className="space-y-2">
												{plan.features.map((feature, i) => (
													<li key={i} className="flex gap-2">
														<Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
														<span className="text-sm dark:text-gray-300">
															{feature}
														</span>
													</li>
												))}
											</ul>
										</CardContent>
										<CardFooter>
											<Button
												className="w-full"
												onClick={() => handleSubscribe(plan.id)}
												disabled={
													isCurrentPlan ||
													isSubscribing ||
													taxInfoQuery.isLoading
												}
												variant={
													isCurrentPlan
														? "outline"
														: isDowngrade
															? "secondary"
															: "default"
												}
											>
												{isSubscribing ? (
													<>
														<Loader2 className="mr-2 h-4 w-4 animate-spin" />
														Redirecting to checkout...
													</>
												) : (
													buttonLabel
												)}
											</Button>
										</CardFooter>
									</Card>
								);
							})
						)}
					</div>

					<div className="mt-8 rounded-lg border bg-muted/50 p-4 dark:border-gray-700 dark:bg-gray-900/30">
						<p className="text-sm dark:text-gray-300">
							Unused monthly credits roll over up to 2x your monthly allocation.
							Perfect for consistent usage with the best value.
						</p>
					</div>
				</TabsContent>
			</Tabs>

			{/* Credit Transaction History */}
			<TransactionHistory className="mt-8" />

			{/* Tax Info Modal */}
			<TaxInfoModal
				open={showTaxModal}
				onOpenChange={setShowTaxModal}
				onSuccess={handleTaxInfoSuccess}
			/>
		</PageContainer>
	);
}
